/**
 * Service version status.
 */
export type ServiceVersionStatus = 'loading' | 'healthy' | 'degraded' | 'unhealthy' | 'unavailable' | 'not-configured';

/**
 * Health/version info for a backend service.
 */
export interface ServiceVersionInfo {
  name: string;
  version: string | null;
  status: ServiceVersionStatus;
  sha?: string;
  healthStatus?: string;
}

/**
 * Service version state for all backend services.
 * Note: S3 Notifier is derived from ApplicationInfo via selector (single source of truth).
 */
export interface ServiceVersionsState {
  loading: boolean;
  web: ServiceVersionInfo;
  api: ServiceVersionInfo;
  pm: ServiceVersionInfo;
  quartz: ServiceVersionInfo;
}

/**
 * Create initial service versions state with all services in loading status.
 * Note: S3 Notifier is derived from ApplicationInfo via selector (single source of truth).
 */
export function createInitialServiceVersionsState(loading: boolean): ServiceVersionsState {
  return {
    loading,
    web: { name: 'Web Application', version: null, status: 'loading' },
    api: { name: 'API Service', version: null, status: 'loading' },
    pm: { name: 'Project Management Service', version: null, status: 'loading' },
    quartz: { name: 'Quartz Scheduler', version: null, status: 'loading' },
  };
}

/**
 * Database type classification, derived server-side from both database name and cluster address.
 * This ensures a local copy of "syrftest" is correctly classified as "development", not "production".
 */
export type DatabaseType = 'production' | 'staging' | 'preview' | 'development' | 'unknown';

/**
 * Application environment information from the API.
 * Used for environment awareness in the UI.
 */
export interface ApplicationInfo {
  /** Runtime environment name (Production, Staging, Preview, Development, Local) */
  environment: string;
  /** MongoDB database name being used */
  databaseName: string;
  /** Where the database is hosted (e.g., "MongoDB Atlas", "Local", "Remote") */
  databaseLocation: string;
  /** Unambiguous database classification derived server-side from name + cluster address */
  databaseType: DatabaseType;
  /** PR number for preview environments (e.g., "2234"). Null for other environments */
  prNumber: string | null;
  /** True if this is the production environment */
  isProduction: boolean;
  /** API service semantic version (e.g., "8.21.0") */
  apiVersion: string;
  /** API service full semantic version including pre-release info */
  apiFullVersion: string;
  /** Short git commit SHA from the monorepo */
  monorepoSha: string;
  /** S3 Notifier Lambda function version (e.g., "0.2.0"). Null if not configured */
  s3NotifierVersion: string | null;
}

/**
 * Environment type for UI styling and behavior.
 */
export type EnvironmentType = 'production' | 'staging' | 'preview' | 'development' | 'local';

/**
 * State shape for the environment feature slice.
 */
export interface EnvironmentState {
  /** The application info from the API (null while loading or on error) */
  info: ApplicationInfo | null;
  /** Whether the environment info is currently loading */
  loading: boolean;
  /** Error message if loading failed */
  error: string | null;
  /** Whether the warning banner has been dismissed this session */
  bannerDismissed: boolean;
  /** Service version information (lazy loaded on dialog open) */
  serviceVersions: ServiceVersionsState | null;
}

/**
 * Initial state for the environment feature slice.
 */
export const initialEnvironmentState: EnvironmentState = {
  info: null,
  loading: false,
  error: null,
  bannerDismissed: false,
  serviceVersions: null,
};
